/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * useNestedDialogs Hook
 *
 * Reusable hook for managing nested dialog states in forms.
 * Handles multiple nested dialogs, form refresh tokens, and parent dialog closing prevention.
 */

"use client";

import { useState } from "react";

/**
 * Hook return type for nested dialog management
 */
export interface UseNestedDialogsReturn {
  /**
   * Open a nested dialog by ID
   */
  openNested: (id: string) => void;

  /**
   * Close a nested dialog by ID
   */
  closeNested: (id: string) => void;

  /**
   * Check if a specific nested dialog is open
   */
  isNestedOpen: (id: string) => boolean;

  /**
   * Check if any nested dialogs are open (used to prevent parent from closing)
   */
  canCloseParent: () => boolean;

  /**
   * Form refresh token (increment to force form re-render)
   */
  formRefreshToken: number;

  /**
   * Increment the form refresh token
   */
  refreshForm: () => void;

  /**
   * Get all open nested dialog IDs
   */
  getOpenDialogs: () => string[];
}

/**
 * Reusable hook for managing nested dialog states
 *
 * @example
 * ```tsx
 * const nestedDialogs = useNestedDialogs();
 *
 * // Open a nested dialog
 * nestedDialogs.openNested('client');
 *
 * // Check if parent can close
 * const canClose = nestedDialogs.canCloseParent();
 *
 * // Refresh form after entity creation
 * nestedDialogs.refreshForm();
 * ```
 */
export function useNestedDialogs(): UseNestedDialogsReturn {
  const [openDialogs, setOpenDialogs] = useState<Set<string>>(new Set());
  const [formRefreshToken, setFormRefreshToken] = useState(0);

  const openNested = (id: string) => {
    setOpenDialogs((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  const closeNested = (id: string) => {
    setOpenDialogs((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const isNestedOpen = (id: string) => {
    return openDialogs.has(id);
  };

  const canCloseParent = () => {
    return openDialogs.size === 0;
  };

  const refreshForm = () => {
    setFormRefreshToken((prev) => prev + 1);
  };

  const getOpenDialogs = () => {
    return Array.from(openDialogs);
  };

  return {
    openNested,
    closeNested,
    isNestedOpen,
    canCloseParent,
    formRefreshToken,
    refreshForm,
    getOpenDialogs,
  };
}
